import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Trash, Plus, Pencil, Check, X } from 'lucide-react';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const StatusManagementModal = ({ isOpen, onClose }) => {
    const { statuses, addStatus, deleteStatus, updateStatus } = usePIM();
    const { items: sortedStatuses, requestSort, sortConfig } = useSortableData(statuses);
    const [newStatusName, setNewStatusName] = useState('');
    const [newStatusHex, setNewStatusHex] = useState('#000000');
    const [newStatusDesc, setNewStatusDesc] = useState('');

    const [editingId, setEditingId] = useState(null);
    const [editFormData, setEditFormData] = useState({ name: '', hex_code: '', description: '' });

    const handleAdd = async () => {
        if (!newStatusName) return;
        await addStatus({ name: newStatusName, hex_code: newStatusHex, description: newStatusDesc });
        setNewStatusName('');
        setNewStatusHex('#000000');
        setNewStatusDesc('');
    };

    const startEdit = (status) => {
        setEditingId(status.id);
        setEditFormData({ name: status.name, hex_code: status.hex_code, description: status.description || '' });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditFormData({ name: '', hex_code: '', description: '' });
    };

    const saveEdit = async (id) => {
        await updateStatus(id, editFormData);
        setEditingId(null);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Manage Statuses">
            <div className="space-y-6">
                {/* Add New Status */}
                <div className="flex flex-col gap-3 bg-stone-900 p-4 rounded-lg border border-stone-700">
                    <div className="flex gap-3">
                        <div className="flex-1 space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">Status Name</label>
                            <Input
                                value={newStatusName}
                                onChange={(e) => setNewStatusName(e.target.value)}
                                placeholder="e.g. Active"
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-stone-500 uppercase">Hex Code</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    value={newStatusHex}
                                    onChange={(e) => setNewStatusHex(e.target.value)}
                                    className="h-9 w-12 rounded bg-transparent cursor-pointer border border-stone-700"
                                />
                                <span className="text-stone-400 text-sm font-mono">{newStatusHex}</span>
                            </div>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-stone-500 uppercase">Description</label>
                        <Input
                            value={newStatusDesc}
                            onChange={(e) => setNewStatusDesc(e.target.value)}
                            placeholder="Optional description..."
                        />
                    </div>
                    <PrimaryButton onClick={handleAdd} className="h-9 w-full justify-center mt-2">
                        <Plus size={16} className="mr-1" /> Add Status
                    </PrimaryButton>
                </div>

                {/* Status List */}
                <div className="flex-1 overflow-hidden flex flex-col border border-stone-700 rounded-lg">
                    <div className="overflow-y-auto max-h-[400px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-stone-950 [&::-webkit-scrollbar-thumb]:bg-stone-700 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-stone-600">
                        <table className="w-full text-left border-collapse">
                            <thead className="bg-stone-950 text-stone-500 text-xs font-bold uppercase sticky top-0 z-10">
                                <tr>
                                    <SortableHeader label="Name" sortKey="name" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Hex" sortKey="hex_code" currentSort={sortConfig} onSort={requestSort} />
                                    <SortableHeader label="Description" sortKey="description" currentSort={sortConfig} onSort={requestSort} />
                                    <th className="px-4 py-3 border-b border-stone-800 text-right w-24">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-stone-800 text-sm text-stone-300 bg-stone-900/50">
                                {sortedStatuses.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="p-4 text-center text-stone-500 italic">No statuses defined.</td>
                                    </tr>
                                ) : (
                                    sortedStatuses.map((status) => (
                                        <tr key={status.id} className="hover:bg-stone-800/50 transition-colors">
                                            {editingId === status.id ? (
                                                <>
                                                    <td className="p-2">
                                                        <Input
                                                            value={editFormData.name}
                                                            onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                                            className="h-8 text-xs"
                                                            autoFocus
                                                        />
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="color"
                                                                value={editFormData.hex_code}
                                                                onChange={(e) => setEditFormData({ ...editFormData, hex_code: e.target.value })}
                                                                className="h-8 w-8 rounded bg-transparent cursor-pointer border border-stone-700"
                                                            />
                                                            <span className="text-xs font-mono text-stone-400">{editFormData.hex_code}</span>
                                                        </div>
                                                    </td>
                                                    <td className="p-2">
                                                        <Input
                                                            value={editFormData.description}
                                                            onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                                                            placeholder="Description"
                                                            className="h-8 text-xs"
                                                        />
                                                    </td>
                                                    <td className="p-2 text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <button
                                                                onClick={() => saveEdit(status.id)}
                                                                className="text-green-500 hover:text-green-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <Check size={14} />
                                                            </button>
                                                            <button
                                                                onClick={cancelEdit}
                                                                className="text-stone-500 hover:text-stone-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <X size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className="p-3 font-medium text-stone-200">{status.name}</td>
                                                    <td className="p-3">
                                                        <div className="flex items-center gap-2">
                                                            <div
                                                                className="w-4 h-4 rounded-full border border-stone-600 shadow-sm"
                                                                style={{ backgroundColor: status.hex_code }}
                                                            ></div>
                                                            <span className="text-xs font-mono text-stone-500">{status.hex_code}</span>
                                                        </div>
                                                    </td>
                                                    <td className="p-3 text-stone-400">{status.description}</td>
                                                    <td className="p-3 text-right">
                                                        <div className="flex justify-end gap-1">
                                                            <button
                                                                onClick={() => startEdit(status)}
                                                                className="text-stone-500 hover:text-blue-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <Pencil size={14} />
                                                            </button>
                                                            <button
                                                                onClick={() => deleteStatus(status.id)}
                                                                className="text-stone-500 hover:text-red-400 p-1.5 rounded hover:bg-stone-700/50 transition"
                                                            >
                                                                <Trash size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Modal>
    );
};
